/**
 * Entity rules on the screen: the reasons on a case, the queue "why" text and the labels
 * of the rules page (docs/goa-integration-concept.md sections 2.5, 2.9 and 3.4).
 *
 * The server evaluates the rules and writes every sentence. This file only orders, groups
 * and labels what it sent, and it never trusts the wire shape: an unknown severity is
 * read as a warning (never as nothing), an entry without a message is dropped.
 *
 * Modes: `block` holds the case, `warn` lists the reason and holds nothing, `info` is a
 * note that is never shown in the queue, `off` means the rule does not run for the entity
 * (the server then sends no reason at all).
 */

import type { BadgeTone } from "@norican/react";

import type { CaseRules, EntityRulesView, RuleReason, RuleRow } from "@/lib/api";

export type RuleSeverity = RuleReason["severity"];
export type RuleMode = RuleRow["mode"];
export type RuleStage = RuleRow["stage"];
export type FreightModel = EntityRulesView["freight_model"];

const SEVERITIES: readonly RuleSeverity[] = ["blocking", "warning", "info"];
const SCOPES: readonly NonNullable<RuleReason["scope"]>[] = ["case", "header", "line"];
const BASES: readonly NonNullable<CaseRules["customer_basis"]>[] = ["confirmed", "engine", "none"];

/** Display order of the severities: what holds the case first. */
const SEVERITY_RANK: Record<RuleSeverity, number> = { blocking: 0, warning: 1, info: 2 };

export const SEVERITY_META: Record<RuleSeverity, { label: string; tone: BadgeTone }> = {
  blocking: { label: "Holds the case", tone: "danger" },
  warning: { label: "Check", tone: "warning" },
  info: { label: "Note", tone: "info" },
};

export const MODE_META: Record<RuleMode, { label: string; tone: BadgeTone; text: string }> = {
  block: {
    label: "Block",
    tone: "danger",
    text: "Holds the case until sales has dealt with it.",
  },
  warn: {
    label: "Warn",
    tone: "warning",
    text: "Lists the reason on the case and in the queue. Nothing is held.",
  },
  info: {
    label: "Note",
    tone: "info",
    text: "Shows a note on the case only. Never shown in the queue.",
  },
  off: { label: "Off", tone: "neutral", text: "The rule does not run for this company." },
};

export const STAGE_LABEL: Record<RuleStage, string> = {
  gate: "Can it be quoted",
  header: "Quotation header",
  pricing: "Prices and freight",
  preflight: "Master data checks",
};

export const FREIGHT_MODEL_LABEL: Record<FreightModel, string> = {
  tiers: "Freight by order value bands",
  in_price: "Freight is in the item price",
  none_at_quote: "No freight on the quotation",
};

export const CUSTOMER_BASIS_TEXT: Record<NonNullable<CaseRules["customer_basis"]>, string> = {
  confirmed: "Checked with the customer sales confirmed.",
  engine: "Checked with the customer I2Q found.",
  none: "No customer yet, so the customer rules are not checked.",
};

/** The sentence shown when the rules changed after the draft was made. */
export const RULES_CHANGED_SENTENCE =
  "The rules changed after this draft was made. The reasons below use the rules active now.";

function text(raw: unknown): string | null {
  return typeof raw === "string" && raw.trim() !== "" ? raw.trim() : null;
}

function isRecord(raw: unknown): raw is Record<string, unknown> {
  return raw != null && typeof raw === "object" && !Array.isArray(raw);
}

function oneOf<T extends string>(values: readonly T[], raw: unknown): T | null {
  return typeof raw === "string" && (values as readonly string[]).includes(raw) ? (raw as T) : null;
}

/** One reason from an untrusted wire entry; null without a rule id or a message. */
function toReason(raw: unknown): RuleReason | null {
  if (!isRecord(raw)) {
    return null;
  }
  const ruleId = text(raw.rule_id);
  const message = text(raw.message);
  if (!ruleId || !message) {
    return null;
  }
  const scope = oneOf(SCOPES, raw.scope) ?? "case";
  const lineKey = text(raw.line_key);
  return {
    rule_id: ruleId,
    // An unknown severity is shown, as a warning: a reason is never silently dropped.
    severity: oneOf(SEVERITIES, raw.severity) ?? "warning",
    entity: text(raw.entity) ?? "",
    message,
    action: text(raw.action) ?? "",
    scope: scope === "line" && !lineKey ? "case" : scope,
    line_key: scope === "line" ? lineKey : null,
    source: text(raw.source) ?? "",
  };
}

/**
 * Blocking, then warning, then info; one entry per rule and line; otherwise the order
 * the server sent (it is stable). Mirrors `rules.reason_list` on the server.
 */
export function orderReasons(reasons: readonly RuleReason[]): RuleReason[] {
  const seen = new Set<string>();
  const unique: RuleReason[] = [];
  for (const reason of reasons) {
    const key = `${reason.rule_id}|${reason.line_key ?? ""}|${reason.message}`;
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(reason);
    }
  }
  return unique
    .map((reason, index) => ({ reason, index }))
    .sort(
      (a, b) =>
        SEVERITY_RANK[a.reason.severity] - SEVERITY_RANK[b.reason.severity] || a.index - b.index,
    )
    .map(({ reason }) => reason);
}

/** The rules block of a case or a preview, from an untrusted answer; null when absent. */
export function toCaseRules(raw: unknown): CaseRules | null {
  if (!isRecord(raw)) {
    return null;
  }
  const reasons = Array.isArray(raw.reasons)
    ? raw.reasons.map(toReason).filter((reason): reason is RuleReason => reason !== null)
    : [];
  return {
    version: text(raw.version) ?? "unavailable",
    stored_version: text(raw.stored_version),
    changed_since_draft: raw.changed_since_draft === true,
    customer_basis: oneOf(BASES, raw.customer_basis) ?? "none",
    reasons: orderReasons(reasons),
  };
}

/** The reasons of one severity. */
export function reasonsOf(rules: CaseRules | null | undefined, severity: RuleSeverity) {
  return (rules?.reasons ?? []).filter((reason) => reason.severity === severity);
}

/** True when the case has a reason that holds it or needs a check. */
export function needsAttention(rules: CaseRules | null | undefined): boolean {
  return (rules?.reasons ?? []).some((reason) => reason.severity !== "info");
}

/** The blocking and warning reasons of one line, in display order. */
export function lineReasons(
  rules: CaseRules | null | undefined,
  lineKey: string | null | undefined,
): RuleReason[] {
  if (!lineKey) {
    return [];
  }
  return (rules?.reasons ?? []).filter(
    (reason) =>
      reason.scope === "line" && reason.line_key === lineKey && reason.severity !== "info",
  );
}

/** At most this many characters in the queue "why" cell (the server writes the same cap). */
export const QUEUE_NOTE_MAX = 80;

/**
 * F&O signal codes in words (360 simulation P27): sales should not need an F&O lookup.
 * Unknown codes stay as they are.
 */
export const SIGNAL_CODE_WORDS: Record<string, string> = {
  REV: "Revision pending, check the drawing",
  NOMOVER: "No movement for a long time",
  DEADM_SP: "Dead stock spare part",
  NEW: "New item, not sold before",
  ECO: "Engineering change open",
};

/** "signal code REV" becomes "signal code Revision pending, check the drawing (REV)". */
export function withSignalWords(message: string): string {
  return message.replace(/\bsignal code ([A-Z][A-Z0-9_]{1,15})\b/g, (whole, code: string) => {
    const words = SIGNAL_CODE_WORDS[code];
    return words ? `signal code "${words}" (${code})` : whole;
  });
}

/** [1, 2, 3, 5, 7, 8] → "1 to 3, 5, 7, 8"; numbers sorted, duplicates dropped. */
export function lineRangeText(numbers: readonly number[]): string {
  const sorted = [...new Set(numbers)].sort((a, b) => a - b);
  const parts: string[] = [];
  for (let i = 0; i < sorted.length; ) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === (sorted[j] ?? 0) + 1) {
      j += 1;
    }
    parts.push(j - i >= 2 ? `${sorted[i]} to ${sorted[j]}` : sorted.slice(i, j + 1).join(", "));
    i = j + 1;
  }
  return parts.join(", ");
}

/** One reason, or several of the same rule and severity on different lines. */
export interface ReasonGroup {
  reasons: RuleReason[];
  /** "Lines 1 to 17" for a group of line reasons; null for a single reason. */
  linesText: string | null;
}

/**
 * The reasons with the line reasons of one rule and severity folded into one group, in
 * the order of their first reason: 17 "no price" messages read as one (360 simulation
 * P27). `lineNumber` gives the shown number of a line key (null when unknown).
 */
export function groupReasons(
  reasons: readonly RuleReason[],
  lineNumber: (lineKey: string) => number | null = () => null,
): ReasonGroup[] {
  const groups: ReasonGroup[] = [];
  const byRule = new Map<string, ReasonGroup>();
  for (const reason of reasons) {
    if (reason.scope !== "line") {
      groups.push({ reasons: [reason], linesText: null });
      continue;
    }
    const key = `${reason.rule_id}|${reason.severity}`;
    const group = byRule.get(key);
    if (group) {
      group.reasons.push(reason);
    } else {
      const next: ReasonGroup = { reasons: [reason], linesText: null };
      byRule.set(key, next);
      groups.push(next);
    }
  }
  for (const group of groups) {
    if (group.reasons.length < 2) {
      continue;
    }
    const numbers = group.reasons
      .map((reason) => {
        const known = reason.line_key ? lineNumber(reason.line_key) : null;
        if (known != null) {
          return known;
        }
        const written = /\bLine (\d+)\b/i.exec(reason.message);
        return written ? Number(written[1]) : null;
      })
      .filter((n): n is number => n != null);
    group.linesText =
      numbers.length > 0 ? `Lines ${lineRangeText(numbers)}` : `${group.reasons.length} lines`;
  }
  return groups;
}

/**
 * The text of the queue "why" cell: the server's review reason, else the rule note.
 * `full` is for the title attribute, `short` for the cell.
 */
export function queueWhy(row: {
  reviewReason?: string | null;
  ruleNote?: string | null;
}): { short: string; full: string } | null {
  const raw = text(row.reviewReason) ?? text(row.ruleNote);
  const full = raw ? withSignalWords(raw) : null;
  if (!full) {
    return null;
  }
  const short =
    full.length <= QUEUE_NOTE_MAX ? full : `${full.slice(0, QUEUE_NOTE_MAX - 3).trimEnd()}...`;
  return { short, full };
}

/**
 * The second line of the queue "why" cell: the rule flags of the case when the first line
 * is the review reason ("3 rule flags: Line 2 has no HSN code (+2 more)"), else null.
 * Shows rule problems (an HSN code, a revision) in the queue, not only on the case.
 */
export function queueRuleLine(row: {
  reviewReason?: string | null;
  ruleNote?: string | null;
  ruleFlagCount?: number | null;
  ruleFlagGroups?: readonly { label: string; count: number }[] | null;
}): string | null {
  const note = text(row.ruleNote);
  if (!text(row.reviewReason) || !note) {
    return null;
  }
  // Grouped per rule (P27): "2 HSN missing, 4 no price" says what is wrong at a glance.
  const groups = (row.ruleFlagGroups ?? []).filter((group) => group.count > 0);
  if (groups.length > 1 || (groups.length === 1 && (groups[0]?.count ?? 0) > 1)) {
    return groups.map((group) => `${group.count} ${group.label}`).join(", ");
  }
  const count = row.ruleFlagCount ?? 0;
  const prefix = count > 0 ? `${count} rule ${count === 1 ? "flag" : "flags"}: ` : "Rules: ";
  return `${prefix}${withSignalWords(note)}`;
}

/** "uk" → "UK", "usl" → "USL"; the label from the server wins when there is one. */
export function entityLabel(entity: string, label?: string | null): string {
  return text(label) ?? entity.toUpperCase();
}

/** Writing rules for every sentence the screen shows about rules (tested). */
export function sentenceProblems(sentence: string): string[] {
  const problems: string[] = [];
  if (/[\u2013\u2014]/.test(sentence)) {
    problems.push("dash");
  }
  if (/\brep\b|\breps\b/i.test(sentence)) {
    problems.push("word"); // say "sales"
  }
  if (!/^[A-Z0-9"]/.test(sentence)) {
    problems.push("start");
  }
  if (!/[.?]$/.test(sentence)) {
    problems.push("end");
  }
  return problems;
}

/**
 * One note from loading the rules. The server sends `{entity, text, source}` (only for
 * the viewer's companies, in business words); an older server sent a plain string that
 * starts with the data file ("freight_tiers.csv: ..."). The file name is kept apart, for
 * the "Source files" disclosure, never in the sentence.
 */
export interface LoadNote {
  /** The company the note is about; null for a note about the whole rule set. */
  entity: string | null;
  text: string;
  /** The data file the note comes from, or null. */
  source: string | null;
}

const FILE_PREFIX = /^([\w.-]+\.(?:csv|ya?ml|json|txt)):\s*(.+)$/i;

/** The load notes from an untrusted `warnings` list; entries without text are dropped. */
export function toLoadNotes(raw: unknown): LoadNote[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((entry): LoadNote[] => {
    if (typeof entry === "string") {
      const line = text(entry);
      if (!line) {
        return [];
      }
      const match = FILE_PREFIX.exec(line);
      return [
        match
          ? { entity: null, text: match[2]!.trim(), source: match[1]! }
          : { entity: null, text: line, source: null },
      ];
    }
    if (!isRecord(entry)) {
      return [];
    }
    const sentence = text(entry.text) ?? text(entry.message);
    if (!sentence) {
      return [];
    }
    const entity = text(entry.entity);
    return [
      {
        entity: entity ? entity.toLowerCase() : null,
        text: sentence,
        source: text(entry.source) ?? text(entry.file),
      },
    ];
  });
}

/**
 * The rows cell of a data table: "3 rows"; for a table that is scoped to other companies
 * (no row here), "Applies to United Kingdom only" instead of "0 rows".
 */
export function dataRowsText(rows: number, appliesTo: readonly string[] = []): string {
  if (rows === 0 && appliesTo.length > 0) {
    return `Applies to ${appliesTo.join(", ")} only`;
  }
  return `${rows} ${rows === 1 ? "row" : "rows"}`;
}
